<template>
   <b-modal
      @hidden="hidden"
      id="modal-project-chat"
      size="xl"
      centered
      ok-variant="primary"
      cancel-variant="light"
      ref="projectModal"
      header-class="font-weight-normal mx-4"
      body-class="mx-4"
      ok-button-class="mx-4 ok-button"
      dialog-class="modal-project-chat"
      no-close-on-backdrop
      no-close-on-esc
   >
      <template #modal-title>
         <h5 style="font-weight: 600; margin-bottom: 0; color: black; font-size: 18px">
            {{ modalState === `projectList` ? `R&D Projects` : `Add New Project` }}
         </h5>
      </template>
      <hr class="full-width-line" />
      <div v-if="modalState === `projectList`">
         <p
            v-if="currentStudyProjects && currentStudyProjects.length === 0"
            class="text-instruction mb-4 modal-subtitle"
         >
            We don't know about your R&D projects yet. You need to add them one by one. Click "Add
            Project" to get started, or just upload a file below and we'll take care of it.
         </p>
         <p v-else class="text-instruction mb-4 modal-subtitle">
            <slot>
               Add any projects missing from this list of {{ studyPeriodsLabel }} projects and
               upload as much supporting documentation as you can. If you prefer, or if several
               projects are missing, please just upload a project list document and we'll take care
               of the rest.
            </slot>
         </p>

         <div
            class="d-flex align-items-center justify-content-between mt-4"
            style="margin-bottom: 10px"
         >
            <label style="font-size: 16px; font-weight: 700">Projects</label>
            <b-button
               v-if="!isRndig"
               variant="primary"
               @click="setChat()"
               class="add-project-button light-button"
            >
               Add Project +
            </b-button>
         </div>
         <div style="height: 350px; overflow-y: auto" class="d-flex flex-column align-items-center">
            <b-spinner v-if="currentStudyProjects === null" variant="primary"></b-spinner>
            <template v-else>
               <div
                  class="project-section"
                  v-for="project in currentStudyProjects"
                  :key="project.key"
               >
                  <img
                     src="@/assets/img/project.png"
                     style="width: 30px; height: 30px; margin-right: 12px"
                     alt="project"
                  />
                  <div style="display: flex; flex-direction: column">
                     <label style="font-size: 14px; font-weight: 600; margin-bottom: 2px">{{
                        project.name
                     }}</label>
                     <label
                        style="font-size: 11px; color: #666666; margin-bottom: 0; font-weight: 400"
                        >{{ project.description ? project.description : ' ' }}</label
                     >
                  </div>
               </div>
            </template>
         </div>
         <div
            class="d-flex align-items-center justify-content-between mt-4"
            style="margin-bottom: 10px"
         >
            <label style="font-size: 16px; font-weight: 700">Project List Document(s)</label>
            <input type="file" ref="fileInput" @input="onFileSelected" style="display: none" />
            <b-button
               variant="primary"
               class="add-project-button light-button"
               @click="$refs.fileInput.click()"
            >
               Add File +
            </b-button>
         </div>
         <FilesList
            v-if="projectCompany"
            category-name="Projects"
            category="PROJECTS"
            :height="200"
            empty-text="No documents uploaded."
            @files-changed="loadData"
         />
      </div>
      <div v-if="modalState === `showChat`">
         <label v-if="!nextDisabled" class="text-instruction mb-2 modal-subtitle"
            >This project appears to meet the qualifications for the R&D credit! Click next to give
            it a name and add it to Onboard.</label
         >
         <div class="d-flex align-items-center" style="padding: 20px 0; gap: 8px; min-width: 100%">
            <div
               class="project-acceptance-card"
               :class="{red: projectCard.state === 0, green: projectCard.state === 1}"
               v-for="(projectCard, index) in projectAccCards"
               :key="index"
            >
               <div
                  :style="`${
                     projectCard.name === 'Permitted Purpose' ? 'width: 120px' : 'width: 155px'
                  }`"
                  class="project-acceptance-card-title"
               >
                  {{ projectCard.name }}

                  <img
                     v-if="projectCard.state === 1"
                     class="acceptance-state"
                     src="@/assets/img/check.png"
                     style="height: 1.2rem; width: 1.2rem"
                     alt="check"
                  />
                  <img
                     v-if="projectCard.state === 0"
                     class="acceptance-state"
                     src="@/assets/img/close.png"
                     style="height: 0.6rem; width: 0.6rem"
                     alt="close"
                  />
               </div>
               <b-icon
                  class="project-acceptance-card-tooltip"
                  style="color: grey"
                  icon="question-circle-fill"
                  v-b-tooltip="projectCard.tooltip"
                  v-b-tooltip.bottom.hover="{customClass: 'chatbot-tooltip'}"
               ></b-icon>
            </div>
         </div>

         <div class="dialog-container">
            <div class="message-container" ref="msgWindow">
               <div
                  v-for="(message, index) in messages"
                  class="pb-4"
                  :key="index"
                  style="width: 100%"
               >
                  <div
                     v-if="message.participant === `client`"
                     class="client-container"
                     style="display: flex; flex-direction: row; align-items: center; gap: 10px"
                  >
                     <label
                        style="width: 450px; text-align: end; font-size: 14px; font-weight: 700"
                        >{{ message.text }}</label
                     >
                     <img
                        v-auth-src="imgSrc"
                        style="width: 2.5rem; border-radius: 50%"
                        alt="icon"
                     />
                  </div>
                  <div
                     v-if="message.participant === `server`"
                     class="server-container"
                     style="display: flex; flex-direction: row; align-items: center; gap: 10px"
                  >
                     <img src="@/assets/img/chat-user.png" style="width: 2.5rem" alt="icon" />
                     <div
                        style="width: 450px; text-align: start; font-size: 14px; font-weight: 400"
                     >
                        <template v-if="message.text !== 'loading'">
                           {{ message.text }}
                        </template>

                        <div class="typing-indicator" v-else>
                           <span class="dot"></span>
                           <span class="dot"></span>
                           <span class="dot"></span>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <div class="text-area-container">
               <label
                  v-if="messages.length === 0"
                  style="
                     display: flex;
                     align-items: center;
                     justify-content: center;
                     text-align: center;
                     font-weight: 300;
                     font-size: 14px;
                  "
                  >Describe a high-level R&D initiative, project, or group of projects your company
                  engaged in during {{ studyPeriodsLabel }}. Onboard will help you describe how it
                  meets the four-part-test for R&D credit qualification, or determine that it is not
                  a qualifying project. If you have any trouble, feel free to just upload your
                  project description as a document instead and we'll take care of the rest.</label
               >
               <div class="input-container">
                  <div class="textarea-wrapper">
                     <b-form-textarea
                        ref="messageInput"
                        v-model="userMessage"
                        @input="updateMessage"
                        @keydown.enter.prevent="sendMessage()"
                        rows="2"
                        :placeholder="
                           messages.length === 0
                              ? 'Start by describing your project here. Onboard will guide you through providing the qualifying information we need.'
                              : nextDisabled
                              ? 'Continue describing how your project meets the 4 part test.'
                              : ''
                        "
                        class="textarea-with-icon custom-placeholder"
                     ></b-form-textarea>
                     <button
                        class="textarea-icon"
                        style="height: 2.5rem; width: 2.5rem; z-index: 100 !important"
                        :style="{cursor: isMessageEmpty ? 'not-allowed' : 'pointer'}"
                        @click="sendMessage()"
                     >
                        <img
                           :src="
                              isMessageEmpty
                                 ? require('@/assets/img/icon-message-arrow.svg')
                                 : require('@/assets/img/icon-message-arrow-active.svg')
                           "
                           style="height: 1.5rem; width: 1.5rem"
                           alt="arrow"
                        />
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <div v-if="modalState === `createProject`">
         <label style="font-size: 16px; font-weight: 700" class="mt-2"> Project Details</label>
         <b-form-row>
            <b-col md="6">
               <b-form-group
                  style="font-size: 13px !important"
                  label="Name:"
                  :invalid-feedback="projectNameInvalidFeedback"
                  label-class="font-weight-normal project-input-field"
               >
                  <b-form-input
                     id="project-name"
                     style="font-size: 13px !important"
                     placeholder="Loading..."
                     ref="projectName"
                     v-model="projectName"
                     :state="projectNameInputState"
                     class="font-weight-normal"
                  ></b-form-input
               ></b-form-group>
            </b-col>
            <b-col md="6">
               <b-form-group
                  style="font-size: 13px !important"
                  label="Type:"
                  placeholder="Loading..."
                  label-class="font-weight-normal project-input-field"
                  label-for="radio-project-type"
                  invalid-feedback="Required"
               >
                  <b-form-select
                     id="select-company"
                     style="font-size: 13px !important"
                     :options="ProjectType"
                     v-model="projectType"
                     class="font-weight-normal"
                  ></b-form-select>
               </b-form-group>
            </b-col>
         </b-form-row>
         <div style="margin: 30px 0; display: flex; flex-direction: column">
            <label style="font-size: 14px; font-weight: 600"> Associated Study Periods</label>
            <label style="font-size: 13px; font-weight: 400">
               Which of these periods did your company work on this project? Select all that apply.
            </label>
         </div>

         <b-form-checkbox-group v-model="selectedPeriods" name="period" stacked>
            <template #first>
               <div v-for="option in periodNames" :key="option" class="project-section pl-4">
                  <b-form-checkbox
                     :value="option"
                     style="font-size: 14px; font-weight: 500; margin-bottom: 0px"
                  >
                     {{ option }}
                  </b-form-checkbox>
               </div>
            </template>
         </b-form-checkbox-group>
      </div>
      <hr class="footer-full-width-line" />
      <template #modal-footer>
         <b-button
            v-if="modalState !== `projectList`"
            variant="outline-primary"
            class="back-button"
            @click="backState"
         >
            Back
         </b-button>
         <b-button
            v-if="modalState !== `projectList`"
            class="my-custom-ok-btn"
            :class="{
               'btn-disabled': nextDisabled,
               'dark-button': modalState === `projectList`,
               'light-button': modalState === 'createProject' || modalState === 'showChat',
            }"
            variant="primary"
            :disabled="nextDisabled"
            @click="changeState"
         >
            {{ modalState === `createProject` ? `Add Project` : `Next` }}
            <b-spinner v-if="creatingProject" small type="border" class="ml-1" />
         </b-button>
      </template>
   </b-modal>
</template>

<script>
import {mapGetters} from 'vuex';
import {ProjectType} from '@/store/modules/projects';
import {normalizeStringInputValue} from '@/helpers/utils';
import FilesList from '@/views/expenses/components/FilesList.vue';

export default {
   components: {FilesList},
   data() {
      return {
         projectCompany: null,
         isMessageEmpty: true,
         messages: [],
         selectedPeriods: [],
         creatingProject: false,
         projectName: '',
         projectType: '',
         chatId: undefined,
         ProjectType,
         modalState: 'projectList',
         userMessage: '',
         currentStudyProjects: null,
         projectAccCards: [
            {
               name: 'Permitted Purpose',
               state: -1,
               tooltip:
                  'To qualify for R&D credit, the purpose of the effort must be to achieve a new or improved product or process. The product or process must provide new or improved functionality, performance, reliability, scalability, and/or overall increase in quality. Efforts with this purpose will qualify, whereas simple aesthetic changes, for example, will not.',
            },
            {
               name: 'Technological in Nature',
               state: -1,
               tooltip:
                  "Your company's development efforts must fundamentally rely on a hard science to qualify as research & development. Efforts that apply engineering disciplines, physics, chemistry, biological science, software engineering, material science, food science, etc. will qualify, whereas soft sciences such as marketing research, sociology, psychology will not.",
            },
            {
               name: 'Elimination of Uncertainty',
               state: -1,
               tooltip:
                  'In order to meet the Elimination of Uncertainty criteria, there must be technical uncertainties or challenges encountered at the onset or throughout the initiative. Uncertainties concerning capability, optimal methodology, or appropriate design of the development or improvement initiatives will likely qualify. Simple fixes, routine repairs & maintenance, or routine production will not.',
            },
            {
               name: 'Process of Experimentation',
               state: -1,
               tooltip:
                  'Finally, your research & development team must be able to demonstrate that the R&D efforts utilized a process which included the evaluation of potential alternatives to achieve the desired outcome. This could include conducting systematic trial-and-error, prototyping and first article development, modeling, or testing to validate design hypotheses and concepts in order to arrive at a final design. A process that did not involve experimentation will not qualify.',
            },
         ],
      };
   },
   computed: {
      ...mapGetters({
         profile: 'profile',
         isRndig: 'isRndig',
         company: 'companies/currentCompany',
         projects: 'projects/projects',
         activeStudyPeriods: 'companies/activeStudyPeriods',
         studyPeriodMap: 'companies/studyPeriodMap',
         uploadCategories: 'uploads/uploadCategories',
      }),
      imgSrc() {
         return {
            src: `/api/user/${this.profile.id}/profile/image`,
            fallbackUrl: require('@/assets/img/client-user.png'),
         };
      },
      studyPeriodsLabel() {
         const studyPeriods = this.activeStudyPeriods.map((period) => period.label);

         if (!studyPeriods || studyPeriods.length === 0) return '';
         if (studyPeriods.length === 1) return studyPeriods[0];
         if (studyPeriods.length === 2) return studyPeriods[0] + ' and ' + studyPeriods[1];
         return (
            studyPeriods.slice(0, -1).join(', ') + ' and ' + studyPeriods[studyPeriods.length - 1]
         );
      },

      periodNames() {
         return this.activeStudyPeriods.map((p) => p.label);
      },
      projectsInStudy() {
         return this.$store.getters['projects/projectsInStudy'] || [];
      },

      normalizedProjectName() {
         return normalizeStringInputValue(this.projectName);
      },

      duplicateProjectName() {
         if (!this.normalizedProjectName) {
            return null;
         }

         const duplicate = this.projectsInStudy.find(
            (project) => normalizeStringInputValue(project.name) === this.normalizedProjectName
         );
         return duplicate ? duplicate.name : null;
      },

      projectNameInputState() {
         if (!this.projectName || this.projectName.trim().length === 0) {
            return null;
         }
         return this.duplicateProjectName ? false : null;
      },

      projectNameInvalidFeedback() {
         if (this.duplicateProjectName) {
            return 'A project with that name already exists';
         }
         return 'Required';
      },

      /** The current company */
      companyId() {
         if (this.$route.params.id) {
            return this.$route.params.id;
         } else {
            return this.company.id;
         }
      },
      nextDisabled() {
         if (this.modalState === 'showChat') {
            return !this.projectAccCards.every((card) => card.state === 1);
         }
         if (this.modalState === 'createProject') {
            return (
               !this.normalizedProjectName ||
               !!this.duplicateProjectName ||
               !this.projectType ||
               this.selectedPeriods.length === 0
            );
         }
         return false;
      },
   },

   methods: {
      closeModal() {
         this.$refs.projectModal.hide();
      },
      hidden() {
         this.modalState = 'projectList';
         this.messages = [];
         this.projectAccCards = [
            {
               name: 'Permitted Purpose',
               state: -1,
               tooltip:
                  'To qualify for R&D credit, the purpose of the effort must be to achieve a new or improved product or process. The product or process must provide new or improved functionality, performance, reliability, scalability, and/or overall increase in quality. Efforts with this purpose will qualify, whereas simple aesthetic changes, for example, will not.',
            },
            {
               name: 'Technological in Nature',
               state: -1,
               tooltip:
                  "Your company's development efforts must fundamentally rely on a hard science to qualify as research & development. Efforts that apply engineering disciplines, physics, chemistry, biological science, software engineering, material science, food science, etc. will qualify, whereas soft sciences such as marketing research, sociology, psychology will not.",
            },
            {
               name: 'Elimination of Uncertainty',
               state: -1,
               tooltip:
                  'In order to meet the Elimination of Uncertainty criteria, there must be technical uncertainties or challenges encountered at the onset or throughout the initiative. Uncertainties concerning capability, optimal methodology, or appropriate design of the development or improvement initiatives will likely qualify. Simple fixes, routine repairs & maintenance, or routine production will not.',
            },
            {
               name: 'Process of Experimentation',
               state: -1,
               tooltip:
                  'Finally, your research & development team must be able to demonstrate that the R&D efforts utilized a process which included the evaluation of potential alternatives to achieve the desired outcome. This could include conducting systematic trial-and-error, prototyping and first article development, modeling, or testing to validate design hypotheses and concepts in order to arrive at a final design. A process that did not involve experimentation will not qualify.',
            },
         ];
         this.$emit('afterClose');
      },
      show() {
         this.$bvModal.show('modal-project-chat');
      },
      updateMessage(value) {
         this.isMessageEmpty = value.trim() === '';
      },
      backState(evt) {
         if (this.modalState === 'showChat') {
            evt.preventDefault();
            this.modalState = 'projectList';
         } else if (this.modalState === 'createProject') {
            evt.preventDefault();
            this.modalState = 'showChat';
         }
         this.messages = [];
         this.projectAccCards = [
            {
               name: 'Permitted Purpose',
               state: -1,
               tooltip:
                  'To qualify for R&D credit, the purpose of the effort must be to achieve a new or improved product or process. The product or process must provide new or improved functionality, performance, reliability, scalability, and/or overall increase in quality. Efforts with this purpose will qualify, whereas simple aesthetic changes, for example, will not.',
            },
            {
               name: 'Technological in Nature',
               state: -1,
               tooltip:
                  "Your company's development efforts must fundamentally rely on a hard science to qualify as research & development. Efforts that apply engineering disciplines, physics, chemistry, biological science, software engineering, material science, food science, etc. will qualify, whereas soft sciences such as marketing research, sociology, psychology will not.",
            },
            {
               name: 'Elimination of Uncertainty',
               state: -1,
               tooltip:
                  'In order to meet the Elimination of Uncertainty criteria, there must be technical uncertainties or challenges encountered at the onset or throughout the initiative. Uncertainties concerning capability, optimal methodology, or appropriate design of the development or improvement initiatives will likely qualify. Simple fixes, routine repairs & maintenance, or routine production will not.',
            },
            {
               name: 'Process of Experimentation',
               state: -1,
               tooltip:
                  'Finally, your research & development team must be able to demonstrate that the R&D efforts utilized a process which included the evaluation of potential alternatives to achieve the desired outcome. This could include conducting systematic trial-and-error, prototyping and first article development, modeling, or testing to validate design hypotheses and concepts in order to arrive at a final design. A process that did not involve experimentation will not qualify.',
            },
         ];
      },
      async onFileSelected(event) {
         const files = event.target.files;
         if (files && files.length > 0) {
            try {
               await this.$store.dispatch('uploads/uploadFiles', {
                  uploadCategoryId: this.projectCompany?.id,
                  files: [files[0]],
               });
            } catch (error) {
               console.log(error);
            }
         }
         this.$refs.fileInput.value = null;
         await this.loadData();
      },
      scrollToBottom() {
         this.$nextTick(() => {
            const container = this.$refs.msgWindow;
            if (!container) return;
            requestAnimationFrame(() => {
               try {
                  container.scrollTop = container.scrollHeight;
                  const last = container.lastElementChild;
                  if (last && typeof last.scrollIntoView === 'function') {
                     last.scrollIntoView({block: 'end'});
                  }
               } catch (e) {
                  console.error(e);
               }
            });
         });
      },
      async changeState(evt) {
         if (this.modalState === 'showChat') {
            evt.preventDefault();
            this.modalState = 'createProject';
            try {
               const response = await this.$store.dispatch('projects/summarizeProjectEvaluation', {
                  chatId: this.chatId,
               });
               const {projectName, ptype} = response;
               this.projectName = projectName;
               this.projectType = ptype;
            } catch (error) {
               if (error) {
                  console.log(error);
               }
            }
         } else if (this.modalState === 'projectList') {
            this.closeModal();
         } else if (this.modalState === 'createProject') {
            evt.preventDefault();

            this.currentStudyProjects = null;
            try {
               this.creatingProject = true;
               await this.$store.dispatch('projects/createProjectFromEvaluation', {
                  companyId: this.companyId,
                  projectName: this.projectName.trim(),
                  projectType: this.projectType,
                  projectEvaluationId: this.chatId,
                  speriodIds: this.activeStudyPeriods
                     .filter((period) => this.selectedPeriods.includes(period.label))
                     .map((period) => period.id),
               });
               this.creatingProject = false;
               this.modalState = 'projectList';
               this.projectName = '';
               this.projectType = '';
               await this.loadData();
            } catch (error) {
               if (error) {
                  console.log(error);
                  this.creatingProject = false;
                  this.modalState = 'projectList';
               }
            }
         }
         this.messages = [];
         this.projectAccCards = [
            {
               name: 'Permitted Purpose',
               state: -1,
               tooltip:
                  'To qualify for R&D credit, the purpose of the effort must be to achieve a new or improved product or process. The product or process must provide new or improved functionality, performance, reliability, scalability, and/or overall increase in quality. Efforts with this purpose will qualify, whereas simple aesthetic changes, for example, will not.',
            },
            {
               name: 'Technological in Nature',
               state: -1,
               tooltip:
                  "Your company's development efforts must fundamentally rely on a hard science to qualify as research & development. Efforts that apply engineering disciplines, physics, chemistry, biological science, software engineering, material science, food science, etc. will qualify, whereas soft sciences such as marketing research, sociology, psychology will not.",
            },
            {
               name: 'Elimination of Uncertainty',
               state: -1,
               tooltip:
                  'In order to meet the Elimination of Uncertainty criteria, there must be technical uncertainties or challenges encountered at the onset or throughout the initiative. Uncertainties concerning capability, optimal methodology, or appropriate design of the development or improvement initiatives will likely qualify. Simple fixes, routine repairs & maintenance, or routine production will not.',
            },
            {
               name: 'Process of Experimentation',
               state: -1,
               tooltip:
                  'Finally, your research & development team must be able to demonstrate that the R&D efforts utilized a process which included the evaluation of potential alternatives to achieve the desired outcome. This could include conducting systematic trial-and-error, prototyping and first article development, modeling, or testing to validate design hypotheses and concepts in order to arrive at a final design. A process that did not involve experimentation will not qualify.',
            },
         ];
      },
      async sendMessage() {
         const message = this.userMessage;

         if (message !== '') {
            this.userMessage = '';
            this.updateMessage('');
            this.messages.push(
               {text: message, participant: 'client'},
               {text: 'loading', participant: 'server'}
            );
            this.scrollToBottom();
            try {
               const response = await this.$store.dispatch(
                  'projects/sendProjectEvaluationMessage',
                  {
                     chatId: this.chatId,
                     content: message,
                  }
               );
               setTimeout(() => {
                  const {commentary, experimental, uncertainty, technological, purpose} = response;

                  const serverMessages = commentary.map((item) => ({
                     text: item,
                     participant: 'server',
                  }));
                  const placeholderIndex = this.messages.length - 1;
                  this.messages.splice(placeholderIndex, 1, ...serverMessages);
                  this.scrollToBottom();
                  this.projectAccCards[0].state = purpose;
                  this.projectAccCards[1].state = technological;
                  this.projectAccCards[2].state = uncertainty;
                  this.projectAccCards[3].state = experimental;
               }, 1000);
            } catch (error) {
               if (error) {
                  this.$set(this.messages, this.messages.length - 1, {
                     text: 'A server error occurred. Please inform system administrators.',
                     participant: 'server',
                  });
                  this.projectAccCards[0].state = -1;
                  this.projectAccCards[1].state = -1;
                  this.projectAccCards[2].state = -1;
                  this.projectAccCards[3].state = -1;
               }
            }
         }
      },
      currentStudyProjectsCalc() {
         if (!this.activeStudyPeriods.length || !this.projects.length) {
            this.currentStudyProjects = [];
            return;
         }

         const currentStudyPeriodIds = this.activeStudyPeriods.map((p) => p.id);

         this.currentStudyProjects = this.projects.filter((project) =>
            project.periodIds.some((id) => currentStudyPeriodIds.includes(id))
         );
      },

      async deleteProject(id) {
         await this.blockingRequest('projects/deleteProject', {
            projectId: id,
            force: true,
         });
         await this.loadData();
      },

      async setChat() {
         try {
            const response = await this.$store.dispatch('projects/startProjectEvaluation', {
               companyId: this.companyId,
            });
            const {id} = response;
            this.chatId = id;
         } catch (error) {
            console.log(error);
         }
         this.modalState = 'showChat';
      },
      async loadData() {
         try {
            await this.$store
               .dispatch('uploads/loadCompanyUploadCategories', {
                  companyId: this.companyId,
                  summary: true,
                  force: true,
               })
               .catch((err) => {
                  this.$store.commit('showAlert', {
                     response: err.response,
                     fallbackMsg: 'Failed to load file upload categories',
                  });
               });

            if (this.uploadCategories) {
               const company = this.uploadCategories.find((result) => result.name === 'Projects');

               this.projectCompany = company || null;
            }

            try {
               await this.$store.dispatch('projects/loadProjects', {
                  companyId: this.companyId,
               });

               this.currentStudyProjectsCalc();
            } catch (error) {
               console.log(error);
            }
         } catch (error) {
            console.log(error);
         }
      },
   },
   async created() {
      try {
         await this.loadData();
         this.currentStudyProjectsCalc();
      } finally {
         this.loaded = true;
      }
   },
};
</script>
<style>
.modal-project-chat {
   height: 784px;
   width: 938px;
}
@media (max-width: 1240px) {
   .modal-project-chat {
      min-width: 100% !important;
      margin: 0px !important;
   }
}

.chatbot-tooltip .tooltip-inner {
   background-color: white;
   font-size: 14px;
   border-radius: 4px;
   min-width: 500px;
   color: black;
   padding: 15px;
   border: 1px solid #ccc;
   box-shadow: 0px 4px 20px 0px #005d8b26;
}

.chatbot-tooltip .bs-tooltip-bottom .arrow::before {
   border-bottom-color: white;
}
</style>
<style scoped>
.project-section {
   border-radius: 5px;
   background-color: #f9f9f9;
   padding: 10px 20px;
   margin-bottom: 10px;
   display: flex;
   flex-direction: row;
   min-width: 100%;
   align-items: center;
   height: 60px;
}
.modal-subtitle {
   padding-top: 10px;
   font-size: 14px;
   font-weight: 400;
}

::v-deep .modal-dialog {
   max-width: 80%;
}

.modal-warning {
   font-size: 12px;
   font-weight: 300;
}
.project-acceptance-card {
   position: relative;
   background-color: #f8f8f8;
   border: 1px solid #ccc;
   border-radius: 8px;
   padding: 10px 20px;
   font-weight: bold;
   width: 25%;
   gap: 3rem;
   min-height: 70px;

   .project-acceptance-card-tooltip {
      position: absolute;
      right: 20px;
      top: 10px;
   }

   @media all and (max-width: 1140px) {
      min-height: 60px;
      max-height: 60px;
   }
}

.project-acceptance-card-title {
   font-size: 16px;

   @media all and (max-width: 1140px) {
      font-size: 13px;
      padding-right: 20px;
   }
}
.dialog-container {
   display: flex;
   position: relative;
   align-items: center;
   flex-direction: column;
   padding: 30px 0;
   width: 100%;
   height: 500px;
}

.message-container {
   width: 100%;
   height: 300px;
   overflow-y: auto;
}

.client-container {
   width: 100%;
   justify-content: end;
   align-items: center;
}
.server-container {
   width: 100%;
   justify-content: start;
   align-items: center;
}

.input-container {
   padding-top: 30px;
   width: 600px;
}
.text-area-container {
   display: flex;
   flex-direction: column;
   position: absolute;
   bottom: 1rem;
   padding: 10px 120px;
   align-items: center;
   justify-content: center;
}
.textarea-wrapper {
   position: relative;
   width: 100%;
}

.textarea-with-icon {
   padding-right: 2.5rem; /* space for the icon */
}

.textarea-icon {
   position: absolute;
   bottom: 0.75rem;
   right: 1.8rem;
   color: #888;
   border: transparent;
   background-color: transparent;
}

.custom-placeholder {
   padding: 15px 140px 25px 15px !important;
   max-width: 580px;
   resize: none;
   font-size: 12px;
   font-weight: 400;
}
.custom-placeholder::placeholder {
   color: #888888;
   font-size: 12px;
   font-weight: 400;
   line-height: 20px;
}
.acceptance-state {
   margin-left: 2px;
   display: inline-block;
   vertical-align: middle;
}
.red {
   background-color: #fef9f9;
   border: 1px solid #db3546;
}

.green {
   background-color: #f5fbf8;
   border: 1px solid #21b66b;
}

.full-width-line {
   margin-left: -2.8rem; /* same as modal content padding */
   margin-right: -2.8rem;
   border: none;
   border-top: 1px solid #ccc;
   margin-top: 0;
}

.footer-full-width-line {
   margin-left: -2.8rem; /* same as modal content padding */
   margin-right: -2.8rem;
   border: none;
   border-top: 1px solid #ccc;
   margin-bottom: 0;
}

.my-custom-ok-btn {
   padding: 5px 30px;
   font-size: 14px !important;
   border: 1px solid #1660a9;
}

.back-button {
   padding: 5px 30px;
   font-size: 14px !important;
   background-color: transparent !important;
   color: #1660a9 !important;

   &:hover {
      background-color: transparent !important;
      color: #1660a9 !important;
   }
}

.btn-disabled {
   background-color: #999 !important;
   border-color: #999 !important;
   &:hover {
      background-color: #999 !important;
      border-color: #999 !important;
   }
}

.add-project-button {
   width: 118px;
   height: 30px;
   background-color: #0778cd !important;
   font-size: 12px !important;
   font-weight: 400 !important;
   &:hover {
      background-color: #0778cd !important;
   }
}

.typing-indicator {
   display: flex;
   align-items: center;
   height: 24px;
}

.dot {
   width: 8px;
   height: 8px;
   margin: 0 3px;
   background-color: #999;
   border-radius: 50%;
   animation: blink 1.4s infinite both;
}

.dot:nth-child(2) {
   animation-delay: 0.2s;
}
.dot:nth-child(3) {
   animation-delay: 0.4s;
}

@keyframes blink {
   0%,
   80%,
   100% {
      opacity: 0;
   }
   40% {
      opacity: 1;
   }
}

.project-input-field {
   font-size: 13px !important;
}

::v-deep .modal-header {
   border-bottom: none !important;
   padding-bottom: 0 !important;
}

::v-deep .modal-footer {
   border-top: none !important;
   padding: 0 2.7rem 1.5rem !important;
}

.modal-footer {
   font-size: 20px;
   font-weight: 400;
   padding-right: 2.5rem !important;
   padding-bottom: 15px !important;
   margin-top: -10px !important;
}
.modal-title {
   font-size: 20px !important;
   font-weight: 600;
   color: black;
}
.custom-control-input {
   left: 10px !important;
}
</style>
s
